<template>
  <div
    ref="popupBlock"
    class="d-user-info-popup"
    :class="{
      'top': isPopupOutOfScreen &&isPopupOutOfScreen.top,
      'left': isPopupOutOfScreen && isPopupOutOfScreen.left,
      'in-modal': inModal,
      'narrow': !userInfo,
    }"
    :style="position"
  >
    <div class="pseudo-block" />
    <div
      class="overlay"
      @mouseup.stop.prevent="closePopup"
    />

    <div
      v-if="userInfo"
      class="d-user-info-popup-container"
    >
      <div class="header grid-flex grid-middle grid-no-response">
        <div
          class="background-blur header-background"
          :style="{ 'background-image': `url(${userInfo.cover_url})` }"
        />

        <a
          class="user-info grid-item grid-flex grid-middle grid-no-response"
          :href="userProfileUrl"
          target="_blank"
          @click.stop
        >
          <img
            class="avatar grid-item-shrink space-right-2em"
            :src="userInfo.avatar_url"
            :alt="`${userInfo.firstname} ${userInfo.lastname}`"
          >
          <div class="grid-item">
            <p class="h4 name">{{ userInfo.firstname }} {{ userInfo.lastname }}</p>
            <span class="user-type">{{ userInfo.username | duiUsername }} - {{ userType }}</span>
          </div>
        </a>

        <button
          class="button-md button-icon button-close-white button-translucent grid-item-shrink visible-until-md"
          @click.stop.prevent="closePopup"
        />
      </div>

      <div class="user-content">
        <div
          class="user-about"
        >
          <a
            :href="userProfileUrl"
            target="_blank"
            @click.stop
          >{{ userInfo.about | duiTruncate(110) }}</a>

          <img
            v-if="!!userInfo.best_badge"
            class="user-badge"
            :alt="userInfo.best_badge.name"
            :src="userInfo.best_badge.image_url"
            :title="userInfo.best_badge.name"
          />
        </div>

        <div class="social">
          <span><b>{{ userInfo.follows_count | duiThousands }}</b> Following</span>
          <span><b>{{ userInfo.followers_count | duiThousands }}</b> Followers</span>
          <span v-if="userInfo.is_reviewer"><b>{{ userInfo.reviews_count | duiThousands }}</b> reviews</span>
          <span v-else><b>{{ userInfo.books_count | duiThousands }}</b> books</span>
        </div>

        <DUserCommonFollowers
          v-if="isFeaturedFollowedByUsers()"
          :featured-followers="userInfo.featured_followed_by"
          :followers-amount="userInfo.followed_by_count"
        />
      </div>

      <div class="grid-2 grid-no-response footer">
        <a
          :href="userProfileUrl"
          target="_blank"
          class="button button-sm button-circle grid-item-shrink"
          @click.stop
        >View profile</a>

        <DFollowButton
          v-once
          class="button button-sm grid-item-shrink"
          :is-following="userInfo.is_following"
          :user-id="userInfo.id"
        />
      </div>
    </div>

    <div
      v-if="!userInfo"
      class="d-user-info-popup-container narrow"
    >
      <DuiIconWarning class="icon-yellow space-right-1em" /> This account is closed
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import DFollowButton from '@d-components/d-follow-button/d-follow-button.vue';
import DUserCommonFollowers from '../d-user-common-followers/d-user-common-followers.vue';
import ElementPosition from '@d-shared/utils/element-position';
import URLS from '@d-shared/urls/urls';
import { debounce } from '@dui/helpers/util-funcs';
import { DEBOUNCE_TIME } from '@d-shared/config';
import SASS from '@dui/helpers/sass';

export default {
  components: {
    DFollowButton,
    DUserCommonFollowers,
  },
  props: {
    inModal: {
      type: Boolean,
      default: false,
    },
    link: {
      type: Node,
      required: true,
    },
    userInfo: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    isPopupOutOfScreen: null,
    linkParams: {
      top: null,
      left: null,
      width: null,
      height: null,
    },
    userProfileUrl: null,
    userType: null,
  }),
  computed: {
    position: function() {
      if (window.innerWidth < SASS.screenSize.sm) {
        return {
          top: 0,
          left: 0,
        };
      }

      return {
        top:
          this.isPopupOutOfScreen && this.isPopupOutOfScreen.top
            ? `${this.linkParams.top - 8}px`
            : `${this.linkParams.top + this.linkParams.height}px`,
        left: `${this.linkParams.left}px`,
      };
    },
  },
  beforeDestroy() {
    this.closePopup();
  },
  mounted() {
    this.init();
  },
  methods: {
    addListener() {
      document.addEventListener('mousemove', this.debouncedEvent);
    },
    closePopup() {
      this.removeListener();
      this.$emit('close-popup');
    },
    debouncedEvent: debounce(function(e) {
      this.mouseMoveHandler(e);
    }, DEBOUNCE_TIME.fastest),
    getUserType() {
      if (this.userInfo.is_reviewer) {
        return 'Reviewer';
      }

      if (this.userInfo.is_author) {
        return 'Author';
      }

      return 'Reader';
    },
    async init() {
      const linkRect = this.link.getBoundingClientRect();
      this.$set(this, 'linkParams', {
        top: linkRect.top + window.scrollY,
        left: linkRect.x + window.scrollX,
        height: linkRect.height,
        width: linkRect.width,
      });

      if (this.userInfo) {
        this.$set(this, 'userType', this.getUserType());

        this.$set(
          this,
          'userProfileUrl',
          URLS.app.userProfile.entry.replace('<username>', this.userInfo.username),
        );
      }

      Vue.duiUniqueInstanceManager.ensureUnique('profilePopup', this);
      await this.$nextTick();
      this.isPopupVisible();
      this.addListener();
    },
    isFeaturedFollowedByUsers() {
      return !!(this.userInfo.featured_followed_by && Object.keys(this.userInfo.featured_followed_by).length);
    },
    isPopupVisible() {
      this.$set(this, 'isPopupOutOfScreen', {
        top: ElementPosition.isElementOutOfBottom(this.$refs.popupBlock),
        left: ElementPosition.isElementOutOfRight(this.$refs.popupBlock),
      });
    },
    mouseMoveHandler(e) {
      if (
        e.target.closest('.d-user-link-popup') ||
        e.target.closest('.d-user-info-popup')
      ) {
        return;
      }

      this.closePopup();
    },
    removeListener() {
      document.removeEventListener('mousemove', this.debouncedEvent);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

$z-index-popup-container: 3;
$width-desktop-popup: 320px;
$width-desktop-empty-popup: 200px;

.d-user-info-popup {
  animation-duration: $transition-time-base;
  animation-fill-mode: forwards;
  animation-name: fade-in;
  background: getColor($white);
  box-shadow: $box-shadow-context-menu;
  color: getColor($black);
  font-size: $font-size-sm;
  overflow: hidden;
  position: absolute;
  z-index: $z-index-sticky-actions - 1;

  &.in-modal {
    z-index: $z-index-modal-active + 1;
  }

  @include screenLessThan(md) {
    background: transparent;
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: $z-index-sticky-actions + 1;
  }

  @include screenMoreThan(sm) {
    border-radius: $border-radius-lg;
    position: absolute;
    transform: translateY(#{$_1em});
    width: $width-desktop-popup;

    &.narrow {
      width: $width-desktop-empty-popup;

      .d-user-info-popup-container {
        align-items: center;
        display: flex;
        line-height: 1;
        padding: $_1-5em $_2em;
      }
    }

    &:before {
      content: "";
      height: $_2em;
      left: 0;
      position: absolute;
      right: 0;
      top: -$_2em;
      z-index: 1;
    }

    &.top {
      transform: translateY(calc(-100% - #{$_1em}));

      &:before {
        bottom: -$_2em;
        top: unset;
      }
    }

    &.left {
      transform: translateX(calc(-100%));

      &:before {
        right: -50%;
      }
    }
  }

  .overlay {
    animation-duration: $transition-time-base;
    animation-fill-mode: forwards;
    animation-name: profile-popup-overlay-show;
    background: $color-translucent-black-dark;
    height: 100%;
    left: 0;
    position: absolute;
    top: 100%;
    width: 100%;

    @include screenMoreThan(sm) {
      display: none;
      pointer-events: none;
    }
  }

  @include screenLessThan(md) {
  .d-user-info-popup-container {
      animation-duration: $transition-time-base;
      animation-fill-mode: forwards;
      animation-name: move-up;
      background-color: #fff;
      border-top-left-radius: $border-radius-lg;
      border-top-right-radius: $border-radius-lg;
      bottom: 0;
      left: 0;
      overflow: hidden;
      position: absolute;
      right: 0;
      transform: translate3d(0, 0, 0);
      width: 100%;
      z-index: $z-index-popup-container;

      &.narrow {
        box-sizing: border-box;
        padding: $_3em $_2em;
      }
    }
  }

  .header {
    overflow: hidden;
    padding: $_2em;
    position: relative;

    .button-follow {
      margin-left: auto;
    }

    .button-close-white {
      margin-left: $_1em;
    }

    .user-info {
      color: getColor($white);
      z-index: 1;
    }

    .name {
      color: getColor($white);
      margin-bottom: 0;
    }

    .username {
      display: block;
      margin-bottom: 0;
    }

    .user-type {
      color: getColor($white);
    }

    img {
      border-radius: 50%;
      height: $_6em;
      width: $_6em;
    }
  }

  .header-background {
    transform: scale(1.2);
  }

  .user-content {
    padding: 0 $_2em;
  }

  .user-about {
    display: flex;
    align-items: center;
    border-bottom: $border-width-base solid getColor($grey, base);
    padding: $_2em 0 $_1em;

    & a {
      color: inherit;
      display: block;
      font: inherit;
    }

    .user-badge {
      flex-shrink: 0;
      display: block;
      width: $_10em;
      height: $_10em;
      margin-left: $_1em;
    }
  }

  .social {
    padding: $_1em 0;

    & span:not(:first-child) {
      margin-left: $_2em;
    }
  }

  .footer {
    padding: 0 $_2em $_2em;
  }
}

@keyframes toTop {
  0% {
    opacity: 0;
    top: 100%;
  }
  50% {
    opacity: 0;
    top: 0;
  }
  100% {
    opacity: 1;
    top: 0;
  }
}
</style>
